
import { APP_WILL_MOUNT, APP_WILL_UNMOUNT } from '../base/app';
import { CONFERENCE_JOINED } from '../base/conference';
import { MHConferenceEvents } from '../base/lib-meet-hour';
import { setAudioMuted } from '../base/media';
import { isLocalParticipantForceMutedAudio } from '../base/participants';
import { MiddlewareRegistry, StateListenerRegistry } from '../base/redux';
import { playSound, registerSound, unregisterSound } from '../base/sounds';
import { getLocalMHAudioTrack } from '../base/tracks';
import { TRACK_ADDED } from '../base/tracks/actionTypes';
import {
    hideNotification,
    showNotification
} from '../notifications';

import { setCurrentNotificationUid } from './actions';
import { TALK_WHILE_MUTED_SOUND_ID } from './constants';
import { TALK_WHILE_MUTED_SOUND_FILE } from './sounds';

/**
 * Tracks the current audio level for muted speaking detection.
 */
let currentAudioLevel = -1;

/**
 * Reference to the current local audio track for cleanup.
 */
let localAudioTrack = null;

/**
 * AudioContext used for independent audio level analysis.
 */
let audioContext = null;

/**
 * AnalyserNode for reading audio levels from a cloned unmuted track.
 */
let analyser = null;

/**
 * MediaStream holding the cloned track used for analysis.
 */
let micStream = null;

/**
 * Reads the current audio level from the analyser node and converts to 0-8 scale.
 *
 * @returns {void}
 */
function updateAudioLevel() {
    if (!analyser) {
        currentAudioLevel = -1;

        return;
    }

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    analyser.getByteTimeDomainData(dataArray);

    let maxDeviation = 0;

    for (let i = 0; i < dataArray.length; i++) {
        const deviation = Math.abs(dataArray[i] - 128);

        if (maxDeviation < deviation) {
            maxDeviation = deviation;
        }
    }

    // Normalize to 0-1 range (same logic as LocalStatsCollector)
    const audioLevel = parseFloat((maxDeviation / 128).toFixed(3));

    currentAudioLevel = Math.floor(audioLevel / 0.125);
}

/**
 * Starts independent audio level monitoring using a cloned MediaStreamTrack.
 * The clone is enabled independently of the conference track, allowing us to
 * detect actual microphone input even when the conference track is muted.
 *
 * @param {Object} mhTrack - The MeetHour audio track.
 * @returns {void}
 */
function startAudioLevelMonitoring(mhTrack) {
    if (!mhTrack || mhTrack === localAudioTrack) {
        return;
    }

    // Clean up previous pipeline if exists
    stopAudioLevelMonitoring();
    localAudioTrack = mhTrack;

    const originalTrack = mhTrack.getTrack && mhTrack.getTrack();

    if (!originalTrack || typeof originalTrack.clone !== 'function') {
        return;
    }

    try {
        const clone = originalTrack.clone();

        clone.enabled = true;

        micStream = new MediaStream([ clone ]);

        const AudioContext = window.AudioContext || window.webkitAudioContext;

        if (!AudioContext) {
            return;
        }

        audioContext = new AudioContext();

        const source = audioContext.createMediaStreamSource(micStream);

        analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.5;
        source.connect(analyser);
    } catch (err) {
        // Failed to create audio context for muted speaking detection.
        // eslint-disable-next-line no-console
        console.error('Failed to create audio context for muted speaking detection:', err);
    }
}

/**
 * Stops the independent audio level monitoring and cleans up resources.
 *
 * @returns {void}
 */
function stopAudioLevelMonitoring() {
    if (audioContext) {
        audioContext.close();
        audioContext = null;
    }

    if (analyser) {
        analyser = null;
    }

    if (micStream) {
        micStream.getTracks().forEach(track => track.stop());
        micStream = null;
    }

    localAudioTrack = null;
    currentAudioLevel = -1;
}

/**
 * Checks if the user is trying to speak while muted based on audio level.
 *
 * @param {Function} dispatch - The redux dispatch function.
 * @param {Function} getState - The redux getState function.
 * @returns {void}
 */
async function checkAudioLevelWhileMuted(dispatch, getState) {
    const state = getState();

    // If local participant is force muted, dismiss any existing talk-while-muted popup and do not show it
    if (isLocalParticipantForceMutedAudio(state)) {
        const { currentNotificationUid } = state['features/talk-while-muted'];

        if (currentNotificationUid) {
            dispatch(hideNotification(currentNotificationUid));
            dispatch(setCurrentNotificationUid());
        }

        return;
    }

    // Read current audio level from the independent analyser pipeline
    updateAudioLevel();

    const mhTrack = getLocalMHAudioTrack(state);

    // Check if audio is muted and audio level is above threshold (1)
    if (mhTrack && mhTrack.isMuted() && currentAudioLevel > 1) {
        const { currentNotificationUid } = state['features/talk-while-muted'];

        // Only show notification if one isn't already showing
        if (!currentNotificationUid) {
            const notification = await dispatch(showNotification({
                titleKey: 'toolbar.talkWhileMutedPopup',
                customActionNameKey: 'notify.unmute',
                customActionHandler: () => {
                    if (isLocalParticipantForceMutedAudio(getState())) {
                        return false;
                    }

                    dispatch(setAudioMuted(false));

                    return true;
                }
            }));

            dispatch(playSound(TALK_WHILE_MUTED_SOUND_ID));

            if (notification) {
                dispatch(setCurrentNotificationUid(notification.uid));
            }
        }
    }
}

MiddlewareRegistry.register(store => next => action => {
    const result = next(action);
    const { dispatch, getState } = store;
    const { conference } = action;

    switch (action.type) {
    case APP_WILL_MOUNT:
        dispatch(registerSound(TALK_WHILE_MUTED_SOUND_ID, TALK_WHILE_MUTED_SOUND_FILE));
        break;
    case APP_WILL_UNMOUNT:
        dispatch(unregisterSound(TALK_WHILE_MUTED_SOUND_ID));
        stopAudioLevelMonitoring();
        break;

    case TRACK_ADDED: {
        // When a new local audio track is added (e.g., from settings/preview),
        // update the audio level monitoring to listen to the new track
        const { track } = action;

        if (track?.local && track?.mediaType === 'audio' && track?.mhTrack) {
            startAudioLevelMonitoring(track.mhTrack);
        }
        break;
    }

    case CONFERENCE_JOINED: {
        // Start monitoring audio levels on the local track
        const state = getState();
        const mhTrack = getLocalMHAudioTrack(state);

        if (mhTrack) {
            startAudioLevelMonitoring(mhTrack);
        }

        // Set up interval to check audio level while muted
        const audioLevelCheckInterval = setInterval(() => {
            checkAudioLevelWhileMuted(dispatch, getState);
        }, 500); // Check every 500ms

        /**
         * Handler for TRACK_MUTE_CHANGED conference event.
         * Hides the talk-while-muted notification when the local audio track is unmuted.
         */
        const onTrackMuteChanged = track => {
            const { currentNotificationUid } = getState()['features/talk-while-muted'];

            if (currentNotificationUid && track.isAudioTrack() && track.isLocal() && !track.isMuted()) {
                dispatch(hideNotification(currentNotificationUid));
                dispatch(setCurrentNotificationUid());
            }
        };

        /**
         * Handler for TRACK_ADDED conference event.
         * Starts monitoring when a new local audio track is added.
         */
        const onTrackAdded = track => {
            if (track.isAudioTrack() && track.isLocal()) {
                startAudioLevelMonitoring(track);
            }
        };

        /**
         * Handler for TRACK_REMOVED conference event.
         * Stops monitoring when the local audio track is removed.
         */
        const onTrackRemoved = track => {
            if (track.isAudioTrack() && track.isLocal() && track === localAudioTrack) {
                stopAudioLevelMonitoring();
            }
        };

        /**
         * Handler for TALK_WHILE_MUTED conference event.
         * Shows the notification when VAD-based detection fires.
         */
        const onTalkWhileMuted = async () => {
            if (isLocalParticipantForceMutedAudio(getState())) {
                return;
            }

            const notification = await dispatch(showNotification({
                titleKey: 'toolbar.talkWhileMutedPopup',
                customActionNameKey: 'notify.unmute',
                customActionHandler: () => {
                    if (isLocalParticipantForceMutedAudio(getState())) {
                        return false;
                    }

                    dispatch(setAudioMuted(false));

                    return true;
                }
            }));

            dispatch(playSound(TALK_WHILE_MUTED_SOUND_ID));

            if (notification) {
                // we store the last start muted notification id that we showed,
                // so we can hide it when unmuted mic is detected
                dispatch(setCurrentNotificationUid(notification.uid));
            }
        };

        /**
         * Handler for CONFERENCE_LEFT event.
         * Cleans up all intervals, monitoring, and conference listeners.
         */
        const onConferenceLeft = () => {
            clearInterval(audioLevelCheckInterval);
            stopAudioLevelMonitoring();
            conference.off(MHConferenceEvents.TRACK_MUTE_CHANGED, onTrackMuteChanged);
            conference.off(MHConferenceEvents.TRACK_ADDED, onTrackAdded);
            conference.off(MHConferenceEvents.TRACK_REMOVED, onTrackRemoved);
            conference.off(MHConferenceEvents.TALK_WHILE_MUTED, onTalkWhileMuted);
            conference.off(MHConferenceEvents.CONFERENCE_LEFT, onConferenceLeft);
        };

        conference.on(MHConferenceEvents.TRACK_MUTE_CHANGED, onTrackMuteChanged);
        conference.on(MHConferenceEvents.TRACK_ADDED, onTrackAdded);
        conference.on(MHConferenceEvents.TRACK_REMOVED, onTrackRemoved);
        conference.on(MHConferenceEvents.TALK_WHILE_MUTED, onTalkWhileMuted);
        conference.on(MHConferenceEvents.CONFERENCE_LEFT, onConferenceLeft);

        break;
    }
    }

    return result;
});

/**
 * State listener to dismiss any active talk-while-muted notification
 * when the local participant becomes force muted.
 */
StateListenerRegistry.register(
    state => isLocalParticipantForceMutedAudio(state),
    (isForceMuted, { dispatch, getState }) => {
        if (isForceMuted) {
            const { currentNotificationUid } = getState()['features/talk-while-muted'];

            if (currentNotificationUid) {
                dispatch(hideNotification(currentNotificationUid));
                dispatch(setCurrentNotificationUid());
            }
        }
    }
);

